<!-- resources/js/components/EmailDashboard.vue - VERSÃO FINAL COM TOGGLE DE GRÁFICOS -->
<template>
  <div class="email-dashboard">
    <!-- Header com filtros -->
    <div class="dashboard-header">
      <div class="title-wrapper">
        <div class="icon-circle">
          <i class="fa-solid fa-chart-line"></i>
        </div>
        <h2>Dashboard de Campanhas</h2>
      </div>

      <div class="header-filters">
        <div class="filter-group">
          <label>Campanhas</label>
          <select v-model="selectedCampaign" class="filter-select">
            <option value="all">Todas as Campanhas</option>
            <option v-for="campaign in campaigns" :key="campaign.id" :value="campaign.id">
              {{ campaign.nome }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Período</label>
          <input type="date" v-model="startDate" class="filter-input" />
        </div>

        <div class="filter-group">
          <label>Até</label>
          <input type="date" v-model="endDate" class="filter-input" />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Carregando métricas...</p>
    </div>

    <!-- Erro -->
    <div v-else-if="errorMessage" class="error-container">
      <i class="fa-solid fa-exclamation-triangle"></i>
      <p>{{ errorMessage }}</p>
      <button @click="loadDashboardData" class="retry-button">Tentar Novamente</button>
    </div>

    <!-- Conteúdo -->
    <div v-else>
      <!-- Mensagem quando não há dados -->
      <div v-if="!hasData" class="no-data-container">
        <i class="fa-solid fa-inbox"></i>
        <p>Nenhuma campanha encontrada para o período selecionado</p>
      </div>

      <template v-else>
        <!-- Cards de métricas principais -->
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">ENVIADOS</span>
            </div>
            <div class="metric-value">{{ formatNumber(metrics.sent) }}</div>
            <div class="metric-border metric-border-blue"></div>
          </div>

          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">ENTREGUES</span>
            </div>
            <div class="metric-value">{{ formatNumber(metrics.delivered) }}</div>
            <div class="metric-trend positive" v-if="metrics.deliveryRate > 0">
              <i class="fa-solid fa-arrow-up"></i>
              {{ metrics.deliveryRate }}% taxa de entrega
            </div>
            <div class="metric-border metric-border-green"></div>
          </div>

          <!-- CARD: ERROS -->
          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">NÃO ENTREGUES</span>
            </div>
            <div class="metric-value">{{ formatNumber(metrics.totalErrors) }}</div>
            <div class="metric-trend negative" v-if="metrics.errorRate > 0">
              <i class="fa-solid fa-arrow-down"></i>
              {{ metrics.errorRate }}% taxa de erro
            </div>
            <div class="metric-border metric-border-red"></div>
          </div>

          <!-- CARD: HARD BOUNCES -->
          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">HARD BOUNCES</span>
            </div>
            <div class="metric-value">{{ formatNumber(metrics.hardBounces) }}</div>
            <div class="metric-trend negative" v-if="metrics.hardBounces > 0">
              <i class="fa-solid fa-exclamation-circle"></i>
              Emails inválidos
            </div>
            <div class="metric-border metric-border-dark-red"></div>
          </div>

          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">ABERTURAS ÚNICAS</span>
            </div>
            <div class="metric-value">{{ formatNumber(metrics.opens) }}</div>
            <div class="metric-border metric-border-cyan"></div>
          </div>

          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">CLIQUES ÚNICOS</span>
            </div>
            <div class="metric-value">{{ formatNumber(metrics.clicks) }}</div>
            <div class="metric-border metric-border-purple"></div>
          </div>

          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">TAXA DE ABERTURA</span>
            </div>
            <div class="metric-value">{{ metrics.openRate }}%</div>
            <div class="metric-border metric-border-orange"></div>
          </div>

          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-label">TAXA DE CLIQUES</span>
            </div>
            <div class="metric-value">{{ metrics.clickRate }}%</div>
            <div class="metric-border metric-border-red"></div>
          </div>
        </div>

        <!-- Gráficos -->
        <div class="charts-row">
          <!-- Gráfico de linha: Aberturas e Cliques ao longo do tempo -->
          <div class="chart-container chart-large">
            <h3 class="chart-title">Aberturas e Cliques ao Longo do Tempo</h3>
            <div class="chart-placeholder">
              <apexchart v-if="lineChartOptions.xaxis.categories.length > 0" type="line" width="100%" height="100%"
                :options="lineChartOptions" :series="lineChartSeries"></apexchart>
              <div v-else class="no-chart-data">
                <i class="fa-solid fa-chart-line"></i>
                <p>Sem dados para exibir</p>
              </div>
            </div>
          </div>

          <!-- Gráfico de pizza: Campanhas por Enviados OU Entregabilidade -->
          <div class="chart-container chart-small">
            <div class="chart-header-with-subtitle">
              <div class="chart-header-vertical">
                <h3 class="chart-title">{{ graficoAtivo === 'entregabilidade' ? 'Entregabilidade' : 'Campanhas por Enviados' }}</h3>
                <p class="chart-subtitle" v-if="graficoAtivo === 'campanhas'">Top 4 campanhas com maior quantidade de envios</p>
                <p class="chart-subtitle" v-else>Visão geral de entregas vs. não entregues</p>
                
                <!-- Botão de Alternância (abaixo do subtítulo) -->
                <button @click="alternarGrafico" class="toggle-chart-button-left">
                  <i :class="graficoAtivo === 'entregabilidade' ? 'fa-solid fa-chart-pie' : 'fa-solid fa-envelope-circle-check'"></i>
                  {{ graficoAtivo === 'entregabilidade' ? 'Ver Campanhas' : 'Ver Entregabilidade' }}
                </button>
              </div>
            </div>
            
            <div class="chart-placeholder">
              <!-- Gráfico de Entregabilidade -->
              <apexchart 
                v-if="graficoAtivo === 'entregabilidade' && entregabilidadeChartSeries.length > 0" 
                type="donut" 
                width="100%" 
                height="100%"
                :key="'entregabilidade-' + entregabilidadeChartSeries.join('-')"
                :options="entregabilidadeChartOptions" 
                :series="entregabilidadeChartSeries">
              </apexchart>
              
              <!-- Gráfico de Campanhas -->
              <apexchart 
                v-else-if="graficoAtivo === 'campanhas' && pieChartSeries.length > 0" 
                type="donut" 
                width="100%" 
                height="100%"
                :key="'campanhas-' + pieChartSeries.join('-')"
                :options="pieChartOptions" 
                :series="pieChartSeries">
              </apexchart>
              
              <!-- Sem dados -->
              <div v-else class="no-chart-data">
                <i class="fa-solid fa-chart-pie"></i>
                <p>Sem dados para exibir</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabela de desempenho detalhado -->
        <div class="performance-table-container">
          <div class="table-header">
            <div class="table-title-wrapper">
              <h3 class="table-title">Desempenho Detalhado das Campanhas</h3>

              <!-- Campo de Pesquisa -->
              <div class="search-wrapper">
                <i class="fa-solid fa-search search-icon"></i>
                <input type="text" v-model="searchQuery" placeholder="Pesquisar campanha pelo nome..."
                  class="search-input" />
                <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search-button"
                  title="Limpar pesquisa">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>

            <!-- Botões de Exportação + Paginação -->
            <div class="actions-wrapper">
              <!-- Botões de Exportação -->
              <div class="export-buttons">
                <button @click="exportarExcel" class="export-button export-excel" :disabled="loading">
                  <i class="fa-solid fa-file-excel"></i>
                  Exportar Geral (Excel)
                </button>
              </div>

              <!-- Paginação -->
              <div class="pagination-controls">
                <button @click="previousPage" :disabled="currentPage === 1" class="pagination-button">
                  <i class="fa-solid fa-chevron-left"></i>
                  Anterior
                </button>

                <span class="pagination-info">
                  Página {{ currentPage }} de {{ totalPages }}
                  <span v-if="searchQuery">({{ filteredCampaigns.length }} de {{ detailedCampaigns.length }}
                    campanhas)</span>
                  <span v-else>({{ paginatedCampaigns.length }} de {{ detailedCampaigns.length }} campanhas)</span>
                </span>

                <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-button">
                  Próxima
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="performance-table">
              <thead>
                <tr>
                  <th>DATA DE ENVIO</th>
                  <th>NOME DA CAMPANHA</th>
                  <th>ENVIADOS</th>
                  <th>ENTREGUES</th>
                  <th>ABERTURAS</th>
                  <th>ABERTURAS ÚNICAS</th>
                  <th>CLIQUES</th>
                  <th>CLIQUES ÚNICOS</th>
                  <th>SOFT BOUNCE</th>
                  <th>HARD BOUNCE</th>
                  <th>BLOQUEADO</th>
                  <th>SPAM</th>
                  <th>EMAIL INVÁLIDO</th>
                  <th>DESCADASTRO</th>
                  <th>OUTROS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="paginatedCampaigns.length === 0">
                  <td colspan="15" class="text-center">Nenhuma campanha encontrada</td>
                </tr>
                <tr v-for="campaign in paginatedCampaigns" :key="campaign.id">
                  <td class="text-center">{{ formatDate(campaign.sendDate) }}</td>
                  <td class="campaign-name">
                    <a href="#" @click.prevent="exportarCampanhaIndividual(campaign)" class="campaign-link"
                      :title="'Exportar detalhes de: ' + campaign.name">
                      {{ campaign.name }}
                      <i class="fa-solid fa-file-excel export-icon"></i>
                    </a>
                  </td>
                  <td class="text-center">{{ formatNumber(campaign.sent) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.delivered) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.opens) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.uniqueOpens) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.clicks) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.uniqueClicks) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.softBounces) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.hardBounces) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.blocked) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.spam) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.invalidEmail) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.unsubscribe) }}</td>
                  <td class="text-center">{{ formatNumber(campaign.other) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'EmailDashboard',

  components: {
    apexchart: VueApexCharts
  },

  data() {
    return {
      selectedCampaign: 'all',
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      campaigns: [],
      loading: false,
      errorMessage: '',
      hasData: false,
      metrics: {
        sent: 0,
        delivered: 0,
        deliveryRate: 0,
        opens: 0,
        clicks: 0,
        openRate: 0,
        clickRate: 0,
        hardBounces: 0,
        totalErrors: 0,
        errorRate: 0
      },
      detailedCampaigns: [],
      // Paginação
      currentPage: 1,
      itemsPerPage: 10,
      // Pesquisa
      searchQuery: '',
      // Controle de qual gráfico mostrar
      graficoAtivo: 'entregabilidade', // padrão: entregabilidade
      // ApexCharts - Gráfico de Linha
      lineChartSeries: [
        {
          name: 'Aberturas',
          data: []
        },
        {
          name: 'Cliques',
          data: []
        }
      ],
      lineChartOptions: {
        chart: {
          type: 'line',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        colors: ['#00bcd4', '#9c27b0'],
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        xaxis: {
          categories: []
        },
        yaxis: {
          title: {
            text: 'Quantidade'
          }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center'
        },
        grid: {
          borderColor: '#e9ecef'
        }
      },
      // ApexCharts - Gráfico de Pizza (Campanhas)
      pieChartSeries: [],
      pieChartOptions: {
        chart: {
          type: 'donut',
          events: {
            dataPointMouseEnter: function(event) {
              // Previne qualquer ação no hover
              event.preventDefault();
            },
            legendClick: function(chartContext, seriesIndex, config) {
              // Desabilita click na legenda
              return false;
            }
          }
        },
        colors: ['#00bcd4', '#2196f3', '#4caf50', '#ff9800'],
        labels: [],
        legend: {
          position: 'right',
          fontSize: '12px',
          itemMargin: {
            vertical: 5
          },
          onItemClick: {
            toggleDataSeries: false  // ❌ Desabilita click
          },
          onItemHover: {
            highlightDataSeries: false  // ❌ Desabilita hover
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: false  // ❌ Desabilita o total no centro
              }
            },
            expandOnClick: false  // ❌ Desabilita expansão no click
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + "%"
          }
        },
        states: {
          hover: {
            filter: {
              type: 'none'  // ❌ Desabilita mudança de cor no hover
            }
          },
          active: {
            filter: {
              type: 'none'  // ❌ Desabilita mudança de cor no click
            }
          }
        },
        tooltip: {
          enabled: true,
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const nomeCompleto = w.config.nomes_completos?.[seriesIndex] ||
              w.config.labels[seriesIndex];
            const valor = series[seriesIndex];

            return '<div style="' +
              'background: white;' +
              'border: 1px solid #e0e0e0;' +
              'border-radius: 8px;' +
              'padding: 12px 16px;' +
              'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);' +
              'min-width: 250px;' +
              'white-space: nowrap;' +
              '">' +
              '<div style="' +
              'font-size: 13px;' +
              'color: #333;' +
              'font-weight: 500;' +
              'margin-bottom: 6px;' +
              'white-space: nowrap;' +
              '">' + nomeCompleto + '</div>' +
              '<div style="font-size: 14px; color: #666;">' +
              '<strong style="color: #00bcd4; font-size: 16px;">' +
              valor.toLocaleString('pt-BR') +
              '</strong> envios' +
              '</div>' +
              '</div>';
          },
          followCursor: true,
          theme: 'light'
        }
      },
      // ApexCharts - Gráfico de Entregabilidade
      entregabilidadeChartSeries: [],
      entregabilidadeChartOptions: {
        chart: {
          type: 'donut'
        },
        colors: ['#4caf50', '#f44336'], // Verde para entregue, Vermelho para não entregue
        labels: ['Entregues', 'Não Entregues'],
        legend: {
          position: 'bottom',
          fontSize: '14px'
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#333',
                  formatter: function(val) {
                    return val;
                  }
                },
                value: {
                  show: true,
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#333',
                  formatter: function (val) {
                    // Garante formatação PT-BR com pontos
                    return Number(val).toLocaleString('pt-BR');
                  }
                },
                total: {
                  show: true,
                  label: 'Total Enviados',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#666',
                  formatter: function (w) {
                    // Soma e formata com pontos
                    const total = w.globals.seriesTotals.reduce((a, b) => {
                      return Number(a) + Number(b);
                    }, 0);
                    return total.toLocaleString('pt-BR');
                  }
                }
              }
            }
          }
        },
        states: {
          hover: {
            filter: {
              type: 'none'  // ❌ Desabilita mudança de cor no hover
            }
          },
          active: {
            filter: {
              type: 'none'  // ❌ Desabilita mudança de cor no click
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + "%"
          },
          style: {
            fontSize: '14px',
            fontWeight: 600
          }
        },
        tooltip: {
          enabled: true,
          custom: function({ series, seriesIndex, dataPointIndex, w }) {
            const label = w.config.labels[seriesIndex];
            const valor = series[seriesIndex];
            
            return '<div style="' +
              'background: white;' +
              'border: 1px solid #e0e0e0;' +
              'border-radius: 8px;' +
              'padding: 12px 16px;' +
              'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);' +
              'min-width: 200px;' +
              '">' +
              '<div style="' +
              'font-size: 14px;' +
              'color: #333;' +
              'font-weight: 600;' +
              'margin-bottom: 4px;' +
              '">' + label + '</div>' +
              '<div style="font-size: 16px; color: #666;">' +
              '<strong style="color: ' + (seriesIndex === 0 ? '#4caf50' : '#f44336') + '; font-size: 18px;">' +
              Number(valor).toLocaleString('pt-BR') +
              '</strong> emails' +
              '</div>' +
              '</div>';
          }
        }
      }
    };
  },

  computed: {
    // Campanhas filtradas pela pesquisa
    filteredCampaigns() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.detailedCampaigns;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.detailedCampaigns.filter(campaign =>
        campaign.name.toLowerCase().includes(query)
      );
    },

    // Campanhas paginadas (após filtro)
    paginatedCampaigns() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCampaigns.slice(start, end);
    },

    // Total de páginas (após filtro)
    totalPages() {
      return Math.ceil(this.filteredCampaigns.length / this.itemsPerPage);
    }
  },

  mounted() {
    this.loadCampaigns();
    this.loadDashboardData();
  },

  watch: {
    selectedCampaign() {
      this.currentPage = 1;
      this.loadDashboardData();
    },
    startDate() {
      this.currentPage = 1;
      this.loadDashboardData();
    },
    endDate() {
      this.currentPage = 1;
      this.loadDashboardData();
    },
    searchQuery() {
      this.currentPage = 1;
    }
  },

  methods: {
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    // Alternar entre gráficos
    alternarGrafico() {
      this.graficoAtivo = this.graficoAtivo === 'entregabilidade' ? 'campanhas' : 'entregabilidade';
    },

    getDefaultStartDate() {
      const date = new Date();
      date.setDate(1);
      return date.toISOString().split('T')[0];
    },

    getDefaultEndDate() {
      const date = new Date();
      return date.toISOString().split('T')[0];
    },

    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return value.toLocaleString('pt-BR');
    },

    formatDate(dateString) {
      if (!dateString) return '-';

      try {
        const dateOnly = dateString.split(' ')[0];
        const [year, month, day] = dateOnly.split('-');

        if (!year || !month || !day) {
          console.error('Data inválida:', dateString);
          return '-';
        }

        const date = new Date(year, month - 1, day);

        if (isNaN(date.getTime())) {
          console.error('Data inválida:', dateString);
          return '-';
        }

        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (error) {
        console.error('Erro ao formatar data:', dateString, error);
        return '-';
      }
    },

    async loadCampaigns() {
      try {
        const response = await fetch('/emails/api/dashboard/metricas', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data.lista_campanhas) {
          this.campaigns = result.data.lista_campanhas;
          console.log('📋 Campanhas carregadas:', this.campaigns.length);
        }
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error);
      }
    },

    async loadDashboardData() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const params = new URLSearchParams();

        if (this.selectedCampaign && this.selectedCampaign !== 'all') {
          params.append('campanha_id', this.selectedCampaign);
        }

        if (this.startDate) {
          params.append('data_inicio', this.startDate);
        }

        if (this.endDate) {
          params.append('data_fim', this.endDate);
        }

        const url = `/emails/api/dashboard/metricas?${params.toString()}`;
        console.log('📡 Requisição:', url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const result = await response.json();
        console.log('📊 Resposta da API:', result);

        if (result.success && result.data) {
          this.hasData = result.data.metricas.total_enviados > 0;

          this.metrics = {
            sent: result.data.metricas.total_enviados || 0,
            delivered: result.data.metricas.total_entregues || 0,
            deliveryRate: result.data.taxas.entrega || 0,
            opens: result.data.metricas.total_aberturas_unicas || 0,
            clicks: result.data.metricas.total_cliques_unicos || 0,
            openRate: result.data.taxas.abertura || 0,
            clickRate: result.data.taxas.clique || 0,
            hardBounces: result.data.metricas.total_hard_bounces || 0,
            totalErrors: result.data.metricas.total_erros || 0,
            errorRate: result.data.taxas.erro || 0
          };

          console.log('📈 Métricas:', this.metrics);

          this.detailedCampaigns = (result.data.campanhas || []).map(campanha => ({
            id: campanha.id,
            sendDate: campanha.enviado_em,
            name: campanha.nome,
            sent: campanha.total_enviados || 0,
            delivered: campanha.total_entregues || 0,
            opens: campanha.aberturas_totais || 0,
            uniqueOpens: campanha.aberturas_unicas || 0,
            clicks: campanha.cliques_totais || 0,
            uniqueClicks: campanha.cliques_unicos || 0,
            softBounces: campanha.soft_bounce || 0,
            hardBounces: campanha.hard_bounce || 0,
            blocked: campanha.blocked || 0,
            spam: campanha.spam || 0,
            invalidEmail: campanha.invalid_email || 0,
            unsubscribe: campanha.unsubscribe || 0,
            other: campanha.other || 0
          }));

          console.log('📋 Campanhas detalhadas:', this.detailedCampaigns.length);

          if (this.hasData) {
            this.updateCharts(result.data.graficos || {});
          }
        } else {
          this.hasData = false;
          this.errorMessage = result.message || 'Nenhum dado disponível';
        }
      } catch (error) {
        console.error('Erro ao carregar dados do dashboard:', error);
        this.errorMessage = 'Erro ao carregar dados do dashboard. Tente novamente.';
        this.hasData = false;
      } finally {
        this.loading = false;
      }
    },

    updateCharts(graficos) {
      // Atualiza gráfico de linha
      if (graficos.aberturas_tempo) {
        this.lineChartOptions = {
          ...this.lineChartOptions,
          xaxis: {
            categories: graficos.aberturas_tempo.labels || []
          }
        };

        this.lineChartSeries = [
          {
            name: 'Aberturas',
            data: graficos.aberturas_tempo.aberturas || []
          },
          {
            name: 'Cliques',
            data: graficos.aberturas_tempo.cliques || []
          }
        ];
      }

      // Atualiza gráfico de ENTREGABILIDADE
      if (this.metrics.sent > 0) {
        this.entregabilidadeChartSeries = [
          this.metrics.delivered || 0,      // Entregues
          this.metrics.totalErrors || 0     // Não Entregues
        ];
      }

      // Atualiza gráfico de pizza de CAMPANHAS
      if (graficos.campanhas_por_enviados) {
        const nomesCompletos = graficos.campanhas_por_enviados.nomes_completos ||
          graficos.campanhas_por_enviados.labels || [];

        console.log('🔍 Nomes completos recebidos:', nomesCompletos);
        console.log('📋 Labels truncados:', graficos.campanhas_por_enviados.labels);

        this.pieChartOptions = {
          ...this.pieChartOptions,
          labels: graficos.campanhas_por_enviados.labels || [],
          nomes_completos: nomesCompletos,
          legend: {
            position: 'right',
            fontSize: '12px',
            itemMargin: {
              vertical: 5
            },
            formatter: function (seriesName, opts) {
              return '<span title="' + nomesCompletos[opts.seriesIndex] + '">' +
                seriesName + '</span>';
            }
          }
        };

        this.pieChartSeries = graficos.campanhas_por_enviados.data || [];
      }
    },

    // EXPORTAR TABELA GERAL
    async exportarExcel() {
      try {
        this.loading = true;

        const params = new URLSearchParams();

        if (this.selectedCampaign && this.selectedCampaign !== 'all') {
          params.append('campanha_id', this.selectedCampaign);
        }
        if (this.startDate) {
          params.append('data_inicio', this.startDate);
        }
        if (this.endDate) {
          params.append('data_fim', this.endDate);
        }

        const response = await fetch(`/emails/api/dashboard/exportar-geral?${params.toString()}`, {
          method: 'GET',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error('Erro ao gerar Excel');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard-geral-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log('✅ Excel geral exportado');

      } catch (error) {
        console.error('❌ Erro ao exportar Excel geral:', error);
        alert('Erro ao exportar Excel. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    // EXPORTAR CAMPANHA INDIVIDUAL
    async exportarCampanhaIndividual(campaign) {
      try {
        this.loading = true;

        const response = await fetch(`/emails/api/dashboard/campanha/${campaign.id}/exportar`, {
          method: 'GET',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        });

        if (!response.ok) {
          throw new Error('Erro ao gerar Excel da campanha');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        // Nome do arquivo limpo (COMPLETO - sem truncar)
        const nomeArquivo = campaign.name.replace(/[^a-z0-9]/gi, '_');
        a.download = `${nomeArquivo}_${new Date().toISOString().split('T')[0]}.xlsx`;

        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log('✅ Excel da campanha exportado');

      } catch (error) {
        console.error('❌ Erro ao exportar campanha:', error);
        alert('Erro ao exportar detalhes da campanha. Tente novamente.');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.email-dashboard {
  padding: 30px;
  background: transparent;
  min-height: 100vh;
  color: #333;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #0d6efd;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  margin-top: 20px;
  color: #666;
  font-size: 16px;
}

/* Error */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
}

.error-container i {
  font-size: 3rem;
  color: #dc3545;
}

.error-container p {
  color: #666;
  font-size: 16px;
}

.retry-button {
  padding: 10px 20px;
  background: #0d6efd;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.retry-button:hover {
  background: #0b5ed7;
}

/* No Data */
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
  background: white;
  border-radius: 12px;
  padding: 40px;
}

.no-data-container i {
  font-size: 4rem;
  color: #ccc;
}

.no-data-container p {
  color: #666;
  font-size: 16px;
}

/* No Chart Data */
.no-chart-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 15px;
}

.no-chart-data i {
  font-size: 3rem;
  color: #ccc;
}

.no-chart-data p {
  color: #999;
  font-size: 14px;
}

/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
}

.icon-circle {
  background: linear-gradient(135deg, #00bcd4 0%, #0097a7 100%);
  color: #fff;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.dashboard-header h2 {
  font-size: 1.8rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.header-filters {
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.filter-select,
.filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #333;
  font-size: 14px;
  min-width: 180px;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #00bcd4;
}

/* Métricas */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.metric-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.metric-label {
  font-size: 11px;
  color: #666;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.metric-value {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
}

.metric-trend {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.metric-trend.positive {
  color: #4caf50;
}

.metric-trend.negative {
  color: #dc3545;
}

.metric-border {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.metric-border-blue {
  background: #2196f3;
}

.metric-border-green {
  background: #4caf50;
}

.metric-border-cyan {
  background: #00bcd4;
}

.metric-border-purple {
  background: #9c27b0;
}

.metric-border-orange {
  background: #ff9800;
}

.metric-border-red {
  background: #f44336;
}

.metric-border-dark-red {
  background: #c62828;
}

.metric-border-warning {
  background: #ff5722;
}

/* Gráficos */
.charts-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-container {
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
}

.chart-header-with-subtitle {
  margin-bottom: 15px;
}

/* Header do gráfico com botão */
.chart-header-vertical {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

/* Botão de alternância (à esquerda, abaixo do subtítulo) */
.toggle-chart-button-left {
  padding: 8px 16px;
  background: #00bcd4;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 188, 212, 0.2);
  width: fit-content;
  align-self: flex-start;
  margin-top: 5px;
}

.toggle-chart-button-left:hover {
  background: #0097a7;
  box-shadow: 0 4px 8px rgba(0, 188, 212, 0.3);
  transform: translateY(-1px);
}

.toggle-chart-button-left i {
  font-size: 14px;
}

.chart-subtitle {
  font-size: 12px;
  color: #666;
  margin: 0;
  font-weight: 400;
}

.chart-placeholder {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.chart-placeholder>div {
  width: 100% !important;
  height: 100% !important;
}

/* ❌ FORÇA REMOÇÃO DO HOVER DO APEXCHARTS */
::v-deep .apexcharts-pie-series path {
  transition: none !important;
  filter: none !important;
}

::v-deep .apexcharts-pie-series path:hover {
  filter: none !important;
  opacity: 1 !important;
}

::v-deep .apexcharts-active {
  filter: none !important;
}

/* ❌ CORRIGE HIGHLIGHT DA LEGENDA */
::v-deep .apexcharts-legend-series {
  pointer-events: none;  /* ❌ Desabilita toda interação com a legenda */
  cursor: default !important;
}

::v-deep .apexcharts-legend-series.apexcharts-inactive-legend {
  opacity: 1 !important;  /* Mantém sempre visível */
}

::v-deep .apexcharts-legend-series:not(.apexcharts-inactive-legend) {
  opacity: 1 !important;
}

/* Tabela */
.performance-table-container {
  background: white;
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 20px;
}

.table-title-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 300px;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Campo de Pesquisa */
.search-wrapper {
  position: relative;
  max-width: 400px;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 14px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 38px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #00bcd4;
  box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
}

.search-input::placeholder {
  color: #999;
}

.clear-search-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  transition: all 0.2s;
}

.clear-search-button:hover {
  background: #f0f0f0;
  color: #666;
}

/* Botões de Exportação */
.actions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: flex-end;
}

.export-buttons {
  display: flex;
  gap: 10px;
}

.export-button {
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.export-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.export-button i {
  font-size: 14px;
}

.export-excel {
  background: #28a745;
  color: white;
}

.export-excel:hover:not(:disabled) {
  background: #218838;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
}

/* Paginação */
.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.pagination-button {
  padding: 8px 16px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #00bcd4;
  color: #00bcd4;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
}

.table-responsive {
  overflow-x: auto;
}

.performance-table {
  width: 100%;
  border-collapse: collapse;
}

.performance-table thead {
  background: #f8f9fa;
}

.performance-table th {
  padding: 12px 15px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.performance-table td {
  padding: 15px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
  font-size: 13px;
}

.performance-table td.text-center {
  text-align: center;
}

.performance-table tbody tr:hover {
  background: #f8f9fa;
}

.campaign-name {
  color: #0d6efd;
  font-weight: 500;
  text-align: left !important;
}

.campaign-link {
  color: #0d6efd;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.campaign-link:hover {
  color: #0a58ca;
  text-decoration: underline;
}

.export-icon {
  font-size: 14px;
  color: #28a745;
  opacity: 0;
  transition: opacity 0.2s;
}

.campaign-link:hover .export-icon {
  opacity: 1;
}

.text-center {
  text-align: center;
  color: #999;
  padding: 30px !important;
}

/* Responsividade */
@media (max-width: 1200px) {
  .charts-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .email-dashboard {
    padding: 20px;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
  }

  .header-filters {
    width: 100%;
  }

  .filter-select,
  .filter-input {
    width: 100%;
  }

  .metrics-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .performance-table-container {
    padding: 20px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .table-title-wrapper {
    width: 100%;
  }

  .search-wrapper {
    max-width: 100%;
  }

  .pagination-controls {
    width: 100%;
    flex-wrap: wrap;
  }

  .pagination-info {
    width: 100%;
    text-align: center;
  }
  
  .chart-header-vertical {
    align-items: flex-start;
  }
  
  .toggle-chart-button-left {
    width: 100%;
    justify-content: center;
  }
}
</style>